import dayjs from 'dayjs';
import React, {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Linking, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Skeleton } from '@components/shared/Skeleton';
import { InvoiceRowSkeleton, SkeletonList } from '@components/shared/skeletons';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { Invoice, TeamRole } from '@graphql/generated';
import { useHover } from '@hooks/useHover';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import theme from '@themes/theme';
import { getStructuralRowBackground } from '@utils/hoverStyles';
import { isTeamExpired } from '@utils/teams/paywall';

type FeatureItemProps = {
  icon: NonNullable<ComponentProps<typeof Icon>['name']>;
  title: string;
  description: string;
};

const FeatureItem = ({ icon, title, description }: FeatureItemProps) => (
  <Box flexDirection='row' alignItems='flex-start' mb='m'>
    <Box
      width={40}
      height={40}
      backgroundColor='grey01'
      borderRadius='xxs'
      alignItems='center'
      justifyContent='center'
      marginRight='s'>
      <Icon name={icon} variant='l' color='textPrimary' />
    </Box>
    <Box flex={1}>
      <Text variant='labelEmphasized' color='textSecondary' mb='xxs'>
        {title}
      </Text>
      <Text variant='webBodySecondary' color='textSecondary'>
        {description}
      </Text>
    </Box>
  </Box>
);

const TABLE_FLEX = [23, 38, 23, 10, 6] as const;

type TableHeaderProps = {
  cells: [ReactNode, ReactNode, ReactNode, ReactNode, ReactNode];
};

const TableHeader = ({ cells }: TableHeaderProps) => (
  <Box flexDirection='row' height={40}>
    {TABLE_FLEX.map((flex, i) => (
      <Box key={`${flex}-${i}`} flex={flex} px='m' justifyContent='center'>
        {cells[i]}
      </Box>
    ))}
  </Box>
);

type InvoiceRowProps = {
  isLast: boolean;
  children: ReactNode;
};

// Hoverable wrapper for an invoice table row — extracted so useHover runs per
// row (hooks can't live inside a FlatList renderItem callback). Structural row
// (TA-9005): hover tints to grey01.
const InvoiceRow = ({ isLast, children }: InvoiceRowProps) => {
  const { isHovered, hoverProps } = useHover();
  return (
    <Box
      {...hoverProps}
      mx='l'
      borderLeftWidth={1}
      borderRightWidth={1}
      borderBottomWidth={1}
      borderColor='grey02'
      justifyContent='center'
      height={48}
      backgroundColor={getStructuralRowBackground(isHovered)}
      style={
        isLast
          ? {
              borderBottomLeftRadius: theme.borderRadii.xs,
              borderBottomRightRadius: theme.borderRadii.xs,
            }
          : undefined
      }
      mb={isLast ? 'l' : undefined}>
      {children}
    </Box>
  );
};

const LEFT_FEATURES = [
  {
    icon: 'Folder',
    titleKey: 'unlimitedProjects',
    descKey: 'unlimitedProjectsDesc',
  },
  { icon: 'Hash', titleKey: 'multipleTasks', descKey: 'multipleTasksDesc' },
  {
    icon: 'Users',
    titleKey: 'rolesPermissions',
    descKey: 'rolesPermissionsDesc',
  },
] as const;

const RIGHT_FEATURES = [
  { icon: 'Database', titleKey: 'sharedStorage', descKey: 'sharedStorageDesc' },
  {
    icon: 'Activity1',
    titleKey: 'globalActivityLog',
    descKey: 'globalActivityLogDesc',
  },
  {
    icon: 'FileText',
    titleKey: 'allProjectTemplates',
    descKey: 'allProjectTemplatesDesc',
  },
] as const;

export const InvoiceTab = () => {
  const { t } = useTranslation('models');
  const { selectedTeam: team } = useWebDrawerNavigator();
  const {
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
    setIsCreateProjectOpen,
    setEditProjectUUId,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setIsTeamPersonal,
    setTeamValues,
    setUpgradeConfirmModalOpen,
  } = useWebDrawer();

  const [isRefreshingInvoices, setIsRefreshingInvoices] = useState(false);
  const previousTeamMemberCountRef = useRef<number | null>(null);
  const delayedRefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // getPublicTeam, not getTeam (TA-10850): this tab renders inside
  // TeamDetailPage, which can be open for a viewer who is not a member yet —
  // getTeam answers "The requested team could not be found" for them.
  // `fullTeam` is the Team branch, so it stays undefined for a non-member and
  // the cards below hold rather than reading defaults off the list-query team.
  const { fullTeam: fullTeamData, refetch } = useTeamDetailFromQuery({
    teamUUId: team?.uuid,
    fetchPolicy: 'network-only',
    includePending: true,
  });

  // TA-10280: `team` (selectedTeam context) comes from the list query,
  // which has no `subscriptionDetails` or `invoices` at all — every field
  // below would read as undefined/false off it, showing the wrong real
  // content (e.g. the upsell block, or "No invoices yet" for a team that
  // has invoices) instead of holding for the skeleton. Prefer the full
  // getTeamDetail result (fetched above) once it resolves.
  const subscriptionSource = fullTeamData ?? team;

  const isOnTrial =
    subscriptionSource?.subscriptionDetails?.subscriptionStatus ===
      'trialing' ||
    subscriptionSource?.subscriptionDetails?.isSubscriptionOnTrial === true;

  const isActive =
    subscriptionSource?.subscriptionDetails?.subscriptionStatus === 'active' &&
    !isOnTrial;

  const isExpired = isTeamExpired(subscriptionSource);
  const trialEndsAt = subscriptionSource?.subscriptionDetails?.trialEndsAt;
  const trialEndFormatted = trialEndsAt
    ? dayjs(trialEndsAt).format('MMMM D, YYYY')
    : null;

  const expirationDate =
    subscriptionSource?.subscriptionDetails?.expirationDate;
  const expirationFormatted = expirationDate
    ? dayjs(expirationDate).format('MMMM D, YYYY')
    : null;

  const pricePerSeat =
    parseFloat(subscriptionSource?.subscriptionDetails?.pricePerSeat ?? '0') /
    100;
  const seatCount =
    subscriptionSource?.subscriptionDetails?.currentSeatCount ?? 0;
  const totalMonthly = Number.parseFloat((pricePerSeat * seatCount).toFixed(2));
  const planName =
    subscriptionSource?.subscriptionDetails?.subscriptionPlanName ?? 'Team';

  const goToUpgradeSubscription = () => {
    setIsTaskWebPanelOpen(false);
    setIsTaskWebPanelMaximize(false);
    setIdTaskDetailOpen('');
    setIdProjectOfTaskDetailOpen('');
    setIsCreateProjectOpen(false);
    setEditProjectUUId('');
    setIdTeamOpen(team?.id ?? '');
    setUUIdTeamOpen(team?.uuid ?? '');
    setIsTeamPersonal(team?.personal ?? false);
    setTeamValues(team);
    setUpgradeConfirmModalOpen(true);
  };

  const sortInvoices = (sortUP: boolean) => {
    const number = sortUP ? 1 : -1;
    return [...(subscriptionSource?.invoices || [])].sort(
      (m1, m2) => (dayjs(m2.date).isAfter(dayjs(m1.date)) ? -1 : 1) * number
    );
  };

  const refetchInvoicesWithDelay = useCallback(() => {
    if (delayedRefetchTimeoutRef.current) {
      clearTimeout(delayedRefetchTimeoutRef.current);
    }

    setIsRefreshingInvoices(true);

    const timeout = setTimeout(async () => {
      try {
        await refetch();
      } finally {
        setIsRefreshingInvoices(false);
        delayedRefetchTimeoutRef.current = null;
      }
    }, 3000);

    delayedRefetchTimeoutRef.current = timeout;
  }, [refetch]);

  useEffect(() => {
    const currentMemberCount = subscriptionSource?.users?.length || 0;

    if (
      previousTeamMemberCountRef.current !== null &&
      currentMemberCount > previousTeamMemberCountRef.current
    ) {
      refetchInvoicesWithDelay();
    }

    previousTeamMemberCountRef.current = currentMemberCount;
  }, [subscriptionSource?.users?.length, refetchInvoicesWithDelay]);

  const [invoicesSortByDateUp, setInvoicesSortByDateUp] =
    useState<boolean>(false);
  const [displayInvoices, setDisplayInvoices] = useState<Invoice[]>(
    sortInvoices(invoicesSortByDateUp)
  );

  useEffect(() => {
    setDisplayInvoices(sortInvoices(invoicesSortByDateUp));
  }, [
    subscriptionSource?.invoices?.length,
    subscriptionSource?.invoices,
    invoicesSortByDateUp,
  ]);

  const titleText = (title: string) => (
    <Text variant='webBodyHintSubline' color='grey04'>
      {title.toUpperCase()}
    </Text>
  );

  const tableColumnHeaders: TableHeaderProps['cells'] = [
    <Box flexDirection='row' alignItems='center'>
      {titleText(t('teams.invoiceTab.tableHeaders.date'))}
      <Icon
        name='Sort2'
        color='grey04'
        variant='l'
        marginLeft='xxs'
        marginTop='s'
        onPress={() => {
          setDisplayInvoices(sortInvoices(!invoicesSortByDateUp));
          setInvoicesSortByDateUp(!invoicesSortByDateUp);
        }}
      />
    </Box>,
    titleText(t('teams.invoiceTab.tableHeaders.description')),
    titleText(t('teams.invoiceTab.tableHeaders.amount')),
    titleText(t('teams.invoiceTab.tableHeaders.status')),
    null,
  ];

  const renderListHeader = () => {
    if (!fullTeamData) {
      // TA-10280: skeleton text lines for plan name/price/billing date
      // while subscription data fetches — mirrors renderPlanHeader's
      // two-column layout (plan name + next-billing-date on the left,
      // price + billed-on-date on the right) — and 3 skeleton rows while
      // invoice history fetches — never the "No invoices yet" empty
      // state before the query has resolved.
      return (
        <>
          <Box mx='l'>
            <Box
              testID='invoice-plan-header-skeleton'
              borderRadius='xs'
              borderWidth={1}
              borderColor='grey02'
              flexDirection='row'
              p='l'
              mb='m'>
              <Box flex={1}>
                <Skeleton
                  variant='text'
                  width={100}
                  height={28}
                  style={{ marginBottom: theme.spacing.xs }}
                />
                <Skeleton variant='text' width={160} />
              </Box>
              <Box flex={1} alignItems='flex-end'>
                <Skeleton
                  variant='text'
                  width={80}
                  height={28}
                  style={{ marginBottom: theme.spacing.xs }}
                />
                <Skeleton variant='text' width={140} />
              </Box>
            </Box>
          </Box>
          <Box
            mx='l'
            mb='l'
            borderRadius='xs'
            borderWidth={1}
            borderColor='grey02'
            overflow='hidden'>
            <Box height={40} justifyContent='center' backgroundColor='grey01'>
              <TableHeader cells={tableColumnHeaders} />
            </Box>
            <SkeletonList row={(index) => <InvoiceRowSkeleton key={index} />} />
          </Box>
        </>
      );
    }

    return (
      <>
        <Box mx='l'>
          {renderExpiredBanner()}
          {!isExpired && renderPlanHeader()}
          {!isActive && renderWhatsIncluded()}
          {isOnTrial && isExpired && renderActivatePlan()}
        </Box>

        {displayInvoices.length === 0 ? (
          <Box
            mx='l'
            mb='l'
            borderRadius='xs'
            borderWidth={1}
            borderColor='grey02'
            overflow='hidden'>
            <Box height={40} justifyContent='center' backgroundColor='grey01'>
              <TableHeader cells={tableColumnHeaders} />
            </Box>
            <Box mx='l' alignItems='center' py='l'>
              <Text variant='labelEmphasized' color='textSecondary' mb='xs'>
                {t('teams.invoiceTab.noInvoicesYet')}
              </Text>
              {trialEndFormatted && (
                <Text variant='webSmall' color='grey04' mt='xxxs'>
                  {t('teams.invoiceTab.firstInvoiceIssued', {
                    date: trialEndFormatted,
                  })}
                </Text>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            mx='l'
            borderTopWidth={1}
            borderLeftWidth={1}
            borderRightWidth={1}
            borderColor='grey02'
            overflow='hidden'
            style={{
              borderTopLeftRadius: theme.borderRadii.xs,
              borderTopRightRadius: theme.borderRadii.xs,
            }}>
            <Box height={40} justifyContent='center' backgroundColor='grey01'>
              <TableHeader cells={tableColumnHeaders} />
            </Box>
          </Box>
        )}
      </>
    );
  };

  const renderItem = (item: Invoice) => {
    const isPaid = item.status?.toLowerCase() === 'paid';
    return (
      <TableHeader
        cells={[
          <Text variant='labelSmall' color='textPrimary'>
            {dayjs(item.date).format('MMMM DD, YYYY')}
          </Text>,
          <Text variant='labelSmall' color='textPrimary'>
            {item.description}
          </Text>,
          <Text variant='labelSmall' color='textPrimary'>
            {`$${(item.amount / 100).toFixed(2)}`}
          </Text>,
          isPaid ? (
            <Box flexDirection='row' alignItems='center'>
              <Box
                paddingHorizontal='xs'
                paddingVertical='xxs'
                borderRadius='xxs'
                backgroundColor='greenSecondaryMild'>
                <Text variant='labelSmall' color='greenSecondary'>
                  {t('teams.overview.subscription.paid')}
                </Text>
              </Box>
              <Box flex={1}></Box>
            </Box>
          ) : (
            <Text variant='webBodySecondary' color='textPrimary'>
              {item.status}
            </Text>
          ),
          item.invoiceUrl ? (
            <TouchableOpacity onPress={() => Linking.openURL(item.invoiceUrl)}>
              <Text variant='labelSmall' color='greenSecondary'>
                {t('teams.overview.subscription.view')}
              </Text>
            </TouchableOpacity>
          ) : null,
        ]}
      />
    );
  };

  const renderPlanHeader = () => (
    <Box
      borderRadius='xs'
      flexDirection='row'
      borderWidth={1}
      borderColor='grey02'
      p='l'
      mb='m'>
      <Box flex={1}>
        <Box flexDirection='row' alignItems='center' mb='xs' flexWrap='wrap'>
          <Text variant='heading1' color='textSecondary'>
            {subscriptionSource?.personal === true
              ? t('teams.invoiceTab.basicPlan')
              : t('teams.invoiceTab.teamPlan')}
          </Text>
          {isActive && (
            <Box
              ml='xs'
              paddingHorizontal='xs'
              paddingVertical='xxs'
              borderRadius='xxs'
              backgroundColor='blueMild'>
              <Text variant='webBodySecondary' color='blue'>
                {t('teams.overview.subscription.active')}
              </Text>
            </Box>
          )}
          {isOnTrial && (
            <Box
              ml='xs'
              paddingHorizontal='xs'
              paddingVertical='xxs'
              borderRadius='xxs'
              backgroundColor='greenSecondaryMild'>
              <Text variant='webBodySecondary' color='greenSecondary'>
                {t('teams.invoiceTab.freeTrial')}
              </Text>
            </Box>
          )}
        </Box>
        {isActive && expirationFormatted && (
          <Text variant='webBodySecondary' color='textSecondary'>
            {t('teams.overview.subscription.nextBillingDate', {
              date: expirationFormatted,
            })}
          </Text>
        )}
        {isOnTrial && trialEndFormatted && (
          <Text variant='webBodySecondary' color='textSecondary'>
            {`${t('teams.invoiceTab.trialEnds', {
              date: trialEndFormatted,
            })} · ${t('teams.invoiceTab.noPaymentUntilThen')}`}
          </Text>
        )}
      </Box>

      {!isActive && seatCount > 0 && pricePerSeat > 0 && (
        <Box flex={1}>
          <Box alignItems='flex-end'>
            <Box flexDirection='row' alignItems='baseline'>
              <Text color='textSecondary' variant='heading1'>
                ${totalMonthly}
              </Text>
              <Text variant='webBodySecondary' color='textSecondary'>
                {subscriptionSource?.subscriptionDetails
                  ?.subscriptionInterval === 'year'
                  ? t('teams.invoiceTab.perYear')
                  : t('teams.invoiceTab.perMonth')}
              </Text>
            </Box>
            {trialEndFormatted && (
              <Text mt='xs' variant='webBodySecondary' color='textSecondary'>
                {t('teams.invoiceTab.billedOn', { date: trialEndFormatted })}
              </Text>
            )}
            <Text mt='xs' variant='webBodySecondary' color='textSecondary'>
              {subscriptionSource?.subscriptionDetails?.subscriptionInterval ===
              'year'
                ? t('teams.invoiceTab.membersYearPrice', {
                    count: seatCount,
                    price: Number(pricePerSeat.toFixed(2)),
                  })
                : t('teams.invoiceTab.membersPrice', {
                    count: seatCount,
                    price: Number(pricePerSeat.toFixed(2)),
                  })}
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderWhatsIncluded = () => (
    <Box
      borderRadius='xs'
      borderWidth={1}
      borderColor='grey02'
      px='l'
      pt='l'
      pb='xs'
      mb='m'>
      <Text variant='labelLarge' color='textSecondary' mb='l'>
        {t('teams.invoiceTab.whatsIncluded', { planName })}
      </Text>
      <Box flexDirection='row'>
        <Box flex={1} mr='m'>
          {LEFT_FEATURES.map(({ icon, titleKey, descKey }) => (
            <FeatureItem
              key={titleKey}
              icon={icon}
              title={t(`teams.invoiceTab.features.${titleKey}`)}
              description={t(`teams.invoiceTab.features.${descKey}`)}
            />
          ))}
        </Box>
        <Box flex={1}>
          {RIGHT_FEATURES.map(({ icon, titleKey, descKey }) => (
            <FeatureItem
              key={titleKey}
              icon={icon}
              title={t(`teams.invoiceTab.features.${titleKey}`)}
              description={t(`teams.invoiceTab.features.${descKey}`)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );

  const renderActivatePlan = () => {
    const canUpgradeSubscription =
      subscriptionSource?.currentUserRole === TeamRole.Admin ||
      subscriptionSource?.currentUserRole === TeamRole.Owner;

    if (!isOnTrial || !canUpgradeSubscription) return null;

    return (
      <Box
        borderRadius='xs'
        borderWidth={1}
        borderColor='grey02'
        padding='l'
        mb='m'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'>
        <Box flex={1} mr='m'>
          <Text variant='labelLarge' color='greenSecondary' mb='xs'>
            {t('teams.invoiceTab.activateYourPlanToday')}
          </Text>
          {trialEndFormatted && (
            <Text variant='webBodySecondary' color='textSecondary'>
              {t('teams.invoiceTab.noChargeUntil', { date: trialEndFormatted })}
            </Text>
          )}
        </Box>
        <Button
          variant='primary'
          width={200}
          height={56}
          isSmall
          onPress={() => {
            goToUpgradeSubscription();
          }}
          accessibilityLabel={t('teams.overview.subscription.activatePlan')}>
          {t('teams.overview.subscription.activatePlan')}
        </Button>
      </Box>
    );
  };

  const renderExpiredBanner = () => {
    if (!isExpired) return null;
    return (
      <Box
        borderRadius='xs'
        mb='m'
        backgroundColor='yellowVividMild'
        padding='l'
        borderWidth={1}
        borderColor='grey02'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'>
        <Box flex={1} mr='m'>
          <Text variant='heading1' color='textSecondary' mb='xxs'>
            {subscriptionSource?.personal === true
              ? t('teams.invoiceTab.basicPlan')
              : t('teams.invoiceTab.teamPlan')}
          </Text>
          <Text variant='webBodySecondary' color='textSecondary'>
            {expirationFormatted
              ? t('teams.invoiceTab.expiredBanner.ended', {
                  date: expirationFormatted,
                })
              : t('teams.invoiceTab.expiredBanner.ended', { date: '' })}
          </Text>
        </Box>
        {(subscriptionSource?.currentUserRole === TeamRole.Admin ||
          subscriptionSource?.currentUserRole === TeamRole.Owner) && (
          <Button
            variant='primary'
            width={200}
            height={56}
            isSmall
            onPress={() => {
              goToUpgradeSubscription();
            }}
            accessibilityLabel={t('teams.invoiceTab.expiredBanner.renewPlan')}>
            {t('teams.invoiceTab.expiredBanner.renewPlan')}
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box flex={1} position='relative' mt='xs'>
      <FlatList
        showsVerticalScrollIndicator={false}
        data={displayInvoices}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={renderListHeader}
        renderItem={({ item, index }) => {
          const isLast = index === displayInvoices.length - 1;
          return <InvoiceRow isLast={isLast}>{renderItem(item)}</InvoiceRow>;
        }}
      />

      {isRefreshingInvoices && (
        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1}
          backgroundColor='white'
          opacity={0.9}
          alignItems='center'
          justifyContent='center'>
          <ActivityIndicatorLoading />
          <Text variant='labelSmall' color='textSecondary' mt='xs'>
            {t('teams.invoiceTab.refreshing')}
          </Text>
        </Box>
      )}
    </Box>
  );
};
